/**
 * Modal to confirm resetting a user's password.
 *
 * Extracted from UserManagementPage.tsx (Sub-PR 6.3).
 */

import { Loader2, KeyRound } from 'lucide-react'
import type { User } from '../../types'

interface ResetPasswordModalProps {
  user: User
  formError: string | null
  formLoading: boolean
  onClose: () => void
  onConfirm: () => void
}

export function ResetPasswordModal({
  user,
  formError,
  formLoading,
  onClose,
  onConfirm,
}: ResetPasswordModalProps): React.ReactElement {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl dark:bg-stone-800">
        <div className="p-6">
          <div className="mb-4 flex items-center gap-4">
            <div className="rounded-full bg-amber-100 p-3 dark:bg-amber-900/30">
              <KeyRound className="h-6 w-6 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-stone-900 dark:text-stone-100">
                Reset Password
              </h2>
              <p className="text-sm text-stone-500 dark:text-stone-400">
                Issue a new one-time password
              </p>
            </div>
          </div>
          <p className="mb-4 text-stone-700 dark:text-stone-300">
            Are you sure you want to reset the password for{' '}
            <span className="font-semibold">{user.displayName ?? user.username}</span>?
          </p>
          <div className="mb-6 rounded-lg bg-amber-50 p-3 dark:bg-amber-900/20">
            <p className="text-sm text-amber-700 dark:text-amber-300">
              A new password will be generated and shown to you once. The user will be required to
              change it at their next sign-in.
            </p>
          </div>
          {formError && (
            <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-400">
              {formError}
            </div>
          )}
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="rounded-lg px-4 py-2 text-stone-700 hover:bg-stone-100 dark:text-stone-300 dark:hover:bg-stone-700"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={formLoading}
              className="flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-white hover:bg-amber-700 disabled:opacity-50"
            >
              {formLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <KeyRound className="h-4 w-4" />
              )}
              Reset Password
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
